/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import flow from 'lodash.flow';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CluTagUtterancesUiStore } from 'src/clu/appShell/cluTagUtterancesPage/CluTagUtterancesUiStore';
import { CluGptPredictedUtteranceStore } from 'src/clu/core/stores/CluGptPredictedUtteranceStore';
import { CluUtteranceEntityLabel } from 'src/clu/core/stores/CluUtteranceStore';
import { gptPredictedUtteranceStoreToGptPredictedUtteranceSwagger } from 'src/clu/modules/handlers/utterances/cluUtteranceMappers';
import { cluLabelsToLabelingInteractionsLabels, labelingInteractionsLabelsToCluLabels } from 'src/clu/modules/utils/labelingUtils';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { useProgressTracker } from 'src/core/hooks/useProgressTracker';
import { logLabelEntity } from 'src/core/services/telemetry/eventsNamesTemplate';
import { ProgressTrackerStore } from 'src/core/stores/ProgressTrackerStore';
import { addSwallowException } from 'src/core/utils/decorators/addSwallowException';
import { addToast } from 'src/core/utils/decorators/addToast';
import { labelingInteractions } from 'src/luis/projects/utterances/handlers/labelingInteractions';
import { ModelType } from 'src/luis/types/ModelType';
import { DatasetType } from 'src/modules/modelTraining/types/trainingTypes';

export type LabelDto = { startTokenIndex: number; endTokenIndex: number; entityId: string };

const messages = defineMessages({
    saveChangesRunning: { id: 'clu.useUtteranceActions.saveChangesRunning', defaultMessage: 'Saving changes...' },
    saveChangesFailed: { id: 'clu.useUtteranceActions.saveChangesFailed', defaultMessage: 'Failed to save changes' },
    saveChangesCompleted: { id: 'clu.useUtteranceActions.saveChangesCompleted', defaultMessage: 'Changes are saved' },
    deleteUtterancesRunning: { id: 'clu.useUtteranceActions.deleteUtterancesRunning', defaultMessage: 'Deleting utterances...' },
    deleteUtterancesCompleted: { id: 'clu.useUtteranceActions.deleteUtterancesCompleted', defaultMessage: 'Utterances deleted' },
    deleteUtterancesFailed: { id: 'clu.useUtteranceActions.deleteUtterancesFailed', defaultMessage: 'Failed to delete utterances' },
    submitDecisionsCompleted: { id: 'clu.useUtteranceActions.submitDecisionsCompleted', defaultMessage: 'Generation decisions are saved' },
    submitDecisionsRunning: { id: 'clu.useUtteranceActions.submitDecisionsRunning', defaultMessage: 'Submitting generation decisions...' },
    submitDecisionsFailed: { id: 'clu.useUtteranceActions.submitDecisionsFailed', defaultMessage: 'Failed to submit generation decisions' },
    utteranceAlreadyExistsTitle: { id: 'clu.useUtteranceActions.utteranceAlreadyExistsTitle', defaultMessage: 'Utterance already exists.' },
    utteranceAlreadyExistsContent: {
        id: 'clu.useUtteranceActions.utteranceAlreadyExistsContent',
        defaultMessage: 'An utterance with this text already exists.'
    }
});

/**
 * A hook for facilitating using utterance handlers.
 */
export const useCluUtteranceActions = (localStore: CluTagUtterancesUiStore) => {
    const { dispatcher, rootStore } = useGlobalState();
    const { entityStoreHouse, gptPredictionsStore } = rootStore.currentCluProjectStore;
    const { localize } = useLocalization();
    const saveChangesTracker = useProgressTracker({ onComplete: () => localStore.setSavingState('saved') });

    /**
     * Deletes the selected utterances.
     */
    const deleteUtterances = React.useCallback(
        (utteranceIds: number[], tracker: ProgressTrackerStore, dataset?: DatasetType) => {
            tracker.setState('running');
            localStore.removeDisplayedUtteranceStores(utteranceIds, dataset);
            localStore.setSavingState('dirty');
            tracker.setState('completed');
        },
        [dispatcher.dispatch]
    );

    /**
     * Submits a new utterance.
     *
     * @param text The utterance text to add.
     * @param language The utterance text to add.
     * @param labeledIntentName The intent to add the utterance to.
     */
    const submitUtterance = React.useCallback(
        flow(
            async (utteranceData: { labeledIntentName: string; language: string; text: string; dataset: DatasetType }) => {
                const alreadyExistedUtterance = localStore.rawUtterancesJson.find(u => u.text === utteranceData.text);
                if (alreadyExistedUtterance && (alreadyExistedUtterance?.dataset || 'Train') === utteranceData.dataset) {
                    throw new Error();
                }

                const alreadyGeneratedUtterance = alreadyExistedUtterance?.dataset === 'Generated';

                if (alreadyGeneratedUtterance) {
                    const generatedUtteranceStore = localStore.displayedUtterancesStores.find(
                        u => u.text === alreadyExistedUtterance.text
                    ) as CluGptPredictedUtteranceStore;
                    acceptGeneratedUtterance(generatedUtteranceStore);
                } else {
                    localStore.addDisplayedUtteranceStore(utteranceData);
                    localStore.setSavingState('dirty');
                }
            },
            addToast({
                toastData: {
                    failed: {
                        title: localize(messages.utteranceAlreadyExistsTitle),
                        content: localize(messages.utteranceAlreadyExistsContent)
                    }
                }
            }),
            addSwallowException
        ),
        [localStore, rootStore.currentCluProjectStore]
    );

    const acceptGeneratedUtterance = React.useCallback(
        flow(
            async (utteranceStore: CluGptPredictedUtteranceStore) => {
                utteranceStore.setGenerationDecision(utteranceStore.updatedText ? 'Modified' : 'Accepted');

                gptPredictionsStore.setRawUtterances(gptPredictionsStore.rawUtterances.filter(u => u.text !== utteranceStore.text));
                const utteranceData = gptPredictedUtteranceStoreToGptPredictedUtteranceSwagger(utteranceStore, 'Accepted');

                const alreadyExistedUtterance = localStore.rawUtterancesJson.find(
                    u => u.text === (utteranceData.updatedText ?? utteranceData.text)
                );
                if (
                    alreadyExistedUtterance &&
                    (alreadyExistedUtterance?.dataset || 'Train') !== 'Generated' &&
                    (alreadyExistedUtterance?.dataset || 'Train') === utteranceData.dataset
                ) {
                    throw new Error();
                }

                gptPredictionsStore.setAcceptedUtterances([...gptPredictionsStore.acceptedUtterances, utteranceStore]);

                localStore.redistributeDisplayedUtteranceStores([utteranceStore.id], localStore.selectedDataset);

                localStore.setSavingState('dirty');
            },
            addToast({
                toastData: {
                    failed: {
                        title: localize(messages.utteranceAlreadyExistsTitle),
                        content: localize(messages.utteranceAlreadyExistsContent)
                    }
                }
            }),
            addSwallowException
        ),
        [localStore]
    );

    const acceptAllGeneratedUtterance = React.useCallback(
        flow(
            async () => {
                for await (const utterance of gptPredictionsStore.rawUtterances) {
                    await acceptGeneratedUtterance(
                        localStore.displayedUtterancesStores.find(u => u.text === utterance.text) as CluGptPredictedUtteranceStore
                    );
                }
            },
            addToast({ toastData: {} }),
            addSwallowException
        ),
        [localStore]
    );

    const rejectGeneratedUtterance = React.useCallback(
        flow(
            async (utteranceStore: CluGptPredictedUtteranceStore) => {
                if (utteranceStore) {
                    utteranceStore.setGenerationDecision('Rejected');

                    gptPredictionsStore.setRawUtterances(gptPredictionsStore.rawUtterances.filter(u => u.text !== utteranceStore.text));
                    gptPredictionsStore.setRejectedUtterances([...gptPredictionsStore.rejectedUtterances, utteranceStore]);

                    localStore.setSavingState('dirty');
                }
            },
            addToast({ toastData: {} }),
            addSwallowException
        ),
        [localStore]
    );

    const rejectAllGeneratedUtterance = React.useCallback(
        flow(
            async () => {
                for await (const utterance of gptPredictionsStore.rawUtterances) {
                    await rejectGeneratedUtterance(
                        localStore.displayedUtterancesStores.find(u => u.text === utterance.text) as CluGptPredictedUtteranceStore
                    );
                }
            },
            addToast({ toastData: {} }),
            addSwallowException
        ),
        [localStore]
    );

    /**
     * Reassigns selected utterances to the given intent.
     *
     * @param destIntentId The intent id to reassign the
     * utterance to.
     */
    const reassignIntent = React.useCallback(
        (utteranceIds: number[], destinationIntentName: string) => {
            localStore.reassignDisplayedUtteranceStores(utteranceIds, destinationIntentName);

            localStore.setSavingState('dirty');
        },
        [localStore]
    );

    /**
     * Reassigns selected utterances to the given dataset.
     *
     * @param destDataset The dataset to reassign the
     * utterance to.
     */
    const reassignDataset = React.useCallback(
        (utteranceIds: number[], destinationDataset: DatasetType) => {
            localStore.redistributeDisplayedUtteranceStores(utteranceIds, destinationDataset);

            localStore.setSavingState('dirty');
        },
        [localStore]
    );

    const addLabel = React.useCallback(
        (utteranceId: number, label: LabelDto, method: 'brush' | 'menu' | 'createNewEntity') => {
            switch (method) {
                case 'brush':
                    logLabelEntity('Brush', 'clu');
                    break;

                case 'menu':
                    logLabelEntity('Menu', 'clu');
                    break;

                case 'createNewEntity':
                    logLabelEntity('Create', 'clu');
                    break;

                default:
                    break;
            }

            const utteranceStore = localStore.displayedUtterancesStores.find(store => store.id === utteranceId);

            const newLabels: CluUtteranceEntityLabel[] = labelingInteractions(
                {
                    entities: entityStoreHouse.items.map(e => ({ id: e.id, name: e.name, typeId: ModelType.Simple })),
                    labels: utteranceStore.labeledEntities.map(cluLabelsToLabelingInteractionsLabels)
                },
                { name: 'AddLabel', label: { entityId: label.entityId, start: label.startTokenIndex, end: label.endTokenIndex } },
                {
                    mutualExclusiveTopLevel: true
                }
            ).labels.map(labelingInteractionsLabelsToCluLabels);

            localStore.updateDisplayedUtteranceStoreLabels(utteranceId, newLabels);
            localStore.setSavingState('dirty');
        },
        [localStore]
    );

    const removeLabels = React.useCallback(
        (utteranceId: number, labels: LabelDto[]) => {
            const utteranceStore = localStore.displayedUtterancesStores.find(store => store.id === utteranceId);

            const newLabels: CluUtteranceEntityLabel[] = labelingInteractions(
                {
                    entities: entityStoreHouse.items.map(e => ({ id: e.id, name: e.name, typeId: ModelType.Simple })),
                    labels: utteranceStore.labeledEntities.map(cluLabelsToLabelingInteractionsLabels)
                },
                {
                    name: 'RemoveLabels',
                    labels: labels.map(l => ({ entityId: l.entityId, start: l.startTokenIndex, end: l.endTokenIndex }))
                },
                {
                    mutualExclusiveTopLevel: true
                }
            ).labels.map(labelingInteractionsLabelsToCluLabels);

            localStore.updateDisplayedUtteranceStoreLabels(utteranceId, newLabels);
            localStore.setSavingState('dirty');
        },
        [localStore]
    );

    const editUtterance = React.useCallback(
        (utteranceId: number, newData: { labeledIntentName: string; language: string; text: string }, _tracker?: ProgressTrackerStore) => {
            if (!newData.text) {
                return;
            }

            localStore.editDisplayedUtteranceStore(utteranceId, newData);
            localStore.setUtteranceToEditId(null);
            localStore.setSavingState('dirty');
        },
        [localStore]
    );

    const saveUserManualChanges = React.useCallback(() => {
        dispatcher.dispatch('saveCluUtterances', {
            localStore,
            tracker: saveChangesTracker,
            toastData: {
                failed: { title: localize(messages.saveChangesFailed) },
                running: { title: localize(messages.saveChangesRunning) },
                completed: { title: localize(messages.saveChangesCompleted) }
            }
        });
    }, [dispatcher.dispatch]);

    const gptTracker = useProgressTracker({ onComplete: saveUserManualChanges });
    const saveChanges = React.useCallback(() => {
        if (gptPredictionsStore.acceptedUtterances.length || gptPredictionsStore.rejectedUtterances.length) {
            dispatcher.dispatch('submitCluGptDecisions', {
                tracker: gptTracker,
                toastData: {
                    failed: { title: localize(messages.submitDecisionsFailed) },
                    running: { title: localize(messages.submitDecisionsRunning) },
                    completed: { title: localize(messages.submitDecisionsCompleted) }
                }
            });
        } else {
            saveUserManualChanges();
        }
    }, [dispatcher.dispatch]);

    return {
        addLabel,
        saveChanges,
        removeLabels,
        editUtterance,
        reassignIntent,
        reassignDataset,
        submitUtterance,
        deleteUtterances,
        acceptGeneratedUtterance,
        rejectGeneratedUtterance,
        acceptAllGeneratedUtterance,
        rejectAllGeneratedUtterance
    };
};
